import { Fragment, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { Card, Dropdown } from "react-bootstrap";
import Button from "app/storybookComponents/Button";
import {
  selectAllCompanyUsersById,
  selectConfigCatFlag,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import Toggle from "app/components/Toggle";
import {
  getCompanyWeeklyCheckInCustomList,
  getExecutiveWeeklyCheckInCompanyCustomList,
  getWeeklyCheckInCompanySettings,
  selectExecutiveWeeklyCheckInCompanyCustomList,
  selectExternalExecutiveReportRecipients,
  selectGetWeeklyCheckInCompanySettingsStatus,
  selectUpdateWeeklyCheckInCompanySettingsStatus,
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
  selectHarmDetectionAlertRecipients,
  setSampleWeeklyCheckInEmailReportModalState,
  updateWeeklyCheckInCompanySettings,
} from "app/containers/WeeklyCheckInMonitor/slice";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import { getPeopleOrPerson, getS } from "utils/helperFunctions";
import EmailReportToggleWarning from "app/containers/WeeklyCheckInMonitor/Modals/EmailReportToggleWarning";
import {
  CheckInEmailReportType,
  VisibilityDefaultType,
  WeeklyCheckInCadence,
  WeeklyCheckInCompanySettings,
} from "../WeeklyCheckInMonitor/types";
import CheckInCustomListModal from "../WeeklyCheckInMonitor/Modals/CheckInCustomListModal";
import ConfigureWeeklyExecutiveCheckinReportModal from "../WeeklyCheckInMonitor/Modals/ConfigureWeeklyExecutiveCheckinReportModal";
import CheckinCustomListConfirmationModal from "../WeeklyCheckInMonitor/Modals/CheckinCustomListConfirmationModal";
import { getDropdownCheck, getVisibilityDefaultOption } from "./helpers";
import { selectAiFeaturesEnabled, selectDepartments } from "./slice";
import Loading from "app/storybookComponents/Loading";
import { getTotalPeopleInSelectedEntitiesFromCustomList } from "../WeeklyCheckInMonitor/helpers";
import { selectAllUserSubordinates } from "../UserSettings/slice";
import WeeklyCheckInVisibilityDefaultsModal from "../WeeklyCheckInMonitor/Modals/WeeklyCheckInVisibilityDefaultsModal";
import {
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS,
  WEEKLY_CHECK_IN_VISIBILITY_LABELS,
  WEEKLY_CHECK_IN_FEATURE_NAME,
} from "../WeeklyCheckInMonitor/constants";
import DisableWeeklyCheckInConfirmationModal from "../WeeklyCheckInMonitor/Modals/DisableWeeklyCheckInConfirmationModal";
import MultiSelectDropdown from "app/components/Dropdowns/MultiSelectDropdown";
import { SelectedItems } from "app/components/Dropdowns/ChecklistDropdown";
import {
  faCheck,
  faChevronDown,
  faEye,
  faShield,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  CADENCE_OPTIONS,
  WEEKLY_CHECK_IN_VISIBILITY_MODE_LIST_ITEMS,
} from "./constants";
import WeeklyCheckInVisibilityWarningModal from "./Modals/WeeklyCheckInVisibilityWarningModal";
import HarmDetectionWarningModal from "./Modals/HarmDetectionWarningModal";
import HarmDetectionRecipientsModal from "../WeeklyCheckInMonitor/Modals/HarmDetectionRecipientsModal";
import { cn } from "app/lib/utils";
import SeniorManagerReportSettingsSection from "./SeniorManagerReportSettingsSection";
import TeamLeaderReportSettingsSection from "./TeamLeaderReportSettingsSection";
import UpdateCheckInCadenceModal from "./Modals/UpdateCheckInCadenceModal";

const WeeklyCheckInAdminConsoleSettings = () => {
  const dispatch = useAppDispatch();
  // ------------------------ Selectors ------------------------------------
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const isAIServiceChatEnabled = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.aiServiceChatEnabled.savedFlagName)
  );
  const isCheckInHarmDetectionEnabled = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.checkInHarmDetection.savedFlagName)
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const updateWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectUpdateWeeklyCheckInCompanySettingsStatus
  );
  const getWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectGetWeeklyCheckInCompanySettingsStatus
  );
  const companyWeeklyCheckInCustomList = useAppSelector(
    selectWeeklyCheckInCustomList
  );
  const executiveWeeklyCheckInCompanyCustomList = useAppSelector(
    selectExecutiveWeeklyCheckInCompanyCustomList
  );
  const externalExecutiveReportRecipients = useAppSelector(
    selectExternalExecutiveReportRecipients
  );
  const users = useAppSelector(selectAllCompanyUsersById);
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const aiServiceChatEnabled = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.aiServiceChatEnabled.savedFlagName)
  );
  const harmDetectionAlertRecipients = useAppSelector(
    selectHarmDetectionAlertRecipients
  );
  const checkInFrequencyEnabled = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.variableCheckInFrequency.savedFlagName)
  );

  // ------------------------ States ------------------------------------
  const [showCreateCustomListModal, setShowCreateCustomListModal] = useState<{
    isOpen: boolean;
    isEditingExisting?: boolean;
  }>({
    isOpen: false,
  });
  const [
    showConfigureWeeklyExecutiveCheckinReportModal,
    setShowConfigureWeeklyExecutiveCheckinReportModal,
  ] = useState<{
    isOpen: boolean;
    enabledForAllAdmins?: boolean;
  }>({ isOpen: false });
  const [
    showWeeklyCheckInConfirmationModalState,
    setShowWeeklyCheckInConfirmationModalState,
  ] = useState<{
    isOpen: boolean;
    enabledForEveryone?: boolean;
  }>({ isOpen: false });
  const [showEmailReportToggleWarning, setShowEmailReportToggleWarning] =
    useState<{
      isOpen: boolean;
      changingTo?: boolean;
      reportType?: "seniorManager" | "teamLeader";
    }>({
      isOpen: false,
    });
  const [visibilityDefaultModalState, setVisibilityDefaultModalState] =
    useState<{
      isOpen: boolean;
      type?: VisibilityDefaultType;
    }>({
      isOpen: false,
    });
  const [isPrivacyFocusedSettingVisible, setIsPrivacyFocusedSettingVisible] =
    useState(false);
  const [
    showDisableWeeklyCheckInConfirmationModal,
    setShowDisableWeeklyCheckInConfirmationModal,
  ] = useState<boolean>(false);
  const [
    showWeeklyCheckInVisibilityWarningModal,
    setShowWeeklyCheckInVisibilityWarningModal,
  ] = useState<boolean>(false);
  const [showHarmDetectionWarningModal, setShowHarmDetectionWarningModal] =
    useState<boolean>(false);
  const [
    showHarmDetectionRecipientsModal,
    setShowHarmDetectionRecipientsModal,
  ] = useState<boolean>(false);
  const [visibilityModeCardCollapseState, setVisibilityModeCardCollapseState] =
    useState<boolean>(false);
  const [weeklyCheckInCadenceModalState, setWeeklyCheckInCadenceModalState] =
    useState<null | { incomingCadence: WeeklyCheckInCadence }>(null);

  useEffect(() => {
    dispatch(getWeeklyCheckInCompanySettings());
    dispatch(getCompanyWeeklyCheckInCustomList());
    dispatch(getExecutiveWeeklyCheckInCompanyCustomList());
  }, [dispatch]);

  const visibilitySetting = useMemo(
    () =>
      weeklyCheckInCompanySettings?.visibilityDefault ??
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM,
    [weeklyCheckInCompanySettings]
  );

  const getCustomListCheckValue = (
    isChecked: boolean,
    hasCustomList: boolean
  ) => {
    if (isChecked || hasCustomList) {
      return getDropdownCheck(isChecked);
    }
    return <FontAwesomeIcon icon="plus" className="me-2" />;
  };

  const totalPeopleWithEnabledCheckIns = useMemo(() => {
    return getTotalPeopleInSelectedEntitiesFromCustomList(
      companyWeeklyCheckInCustomList,
      allUsersSubordinates,
      teamsById,
      departments
    );
  }, [
    companyWeeklyCheckInCustomList,
    teamsById,
    departments,
    allUsersSubordinates,
  ]);

  const onCustomListDropdownSelection = (eventKey: string | null) => {
    switch (eventKey) {
      case "everyone":
        setShowWeeklyCheckInConfirmationModalState({
          isOpen: true,
          enabledForEveryone: true,
        });
        break;
      case "enable-custom-list":
        setShowWeeklyCheckInConfirmationModalState({
          isOpen: true,
        });
        break;
      case "custom-list":
        setShowCreateCustomListModal({
          isOpen: true,
        });
        break;
    }
  };

  const getWhoIsReceivingCheckInSelectedValue = () => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      const totalLength = Object.keys(users).length;
      return (
        <div>
          <p>{totalLength} people</p>
          <p className="small-grey-text">(Enabled for everyone)</p>
        </div>
      );
    }

    let descriptionTextArray: string[] = [];
    const { managers = [], teams = [] } = companyWeeklyCheckInCustomList ?? {};

    if (managers.length) {
      descriptionTextArray.push(
        `${managers.length} manager${getS(managers.length)}`
      );
    }

    if (teams.length) {
      descriptionTextArray.push(`${teams.length} team${getS(teams.length)}`);
    }

    if (!descriptionTextArray.length) {
      return (
        <div>
          <p>No one so far</p>
        </div>
      );
    }

    return (
      <div>
        <p>
          {totalPeopleWithEnabledCheckIns}{" "}
          {getPeopleOrPerson(totalPeopleWithEnabledCheckIns)}
        </p>
        <p className="small-grey-text">({descriptionTextArray.join(", ")})</p>
      </div>
    );
  };

  const getWhoIsReceivingCheckInDropdownOptions = () => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return [
        <Dropdown.Item eventKey="custom-list" key="custom-list">
          Switch to custom list
        </Dropdown.Item>,
      ];
    }

    const {
      managers = [],
      teams = [],
      departments = [],
    } = companyWeeklyCheckInCustomList ?? {};
    const dropdownOptions = [
      <Dropdown.Divider key="divider" />,
      <Dropdown.Item eventKey="everyone" key="everyone">
        Enable for everyone
      </Dropdown.Item>,
    ];

    if (!managers.length && !teams.length && !departments.length) {
      return [
        <Dropdown.Item eventKey="custom-list" key="custom-list">
          <FontAwesomeIcon icon="plus" className="me-2" />
          Add people
        </Dropdown.Item>,
        ...dropdownOptions,
      ];
    }

    return [
      <Dropdown.Item eventKey="custom-list" key="custom-list">
        <FontAwesomeIcon icon="edit" className="me-2" />
        Edit list
      </Dropdown.Item>,
      ...dropdownOptions,
    ];
  };

  const executiveCustomListDropdownText = useMemo((): string | null => {
    if (!executiveWeeklyCheckInCompanyCustomList?.length) {
      return null;
    }

    let totalAdmins = 0;

    const areAllAdminPartOfCustomList = Object.values(users).every((user) => {
      const isUserAdmin = user?.tmgRoleId === 1;
      if (!isUserAdmin) {
        return true; // If user not admin no need to check if they are part of the custom list.
      }
      totalAdmins++;

      return executiveWeeklyCheckInCompanyCustomList?.includes(
        user.userAccountId
      );
    });

    const totalMemberLength = executiveWeeklyCheckInCompanyCustomList.length;

    if (areAllAdminPartOfCustomList) {
      const remainder = totalMemberLength - totalAdmins;
      return remainder
        ? `(All admins + ${remainder} ${getPeopleOrPerson(remainder)})`
        : "(All admins)";
    }

    return `(${totalMemberLength} ${getPeopleOrPerson(totalMemberLength)})`;
  }, [executiveWeeklyCheckInCompanyCustomList, users]);

  const getCustomListDropdownForExecutiveCheckInReport = () => {
    const isCustomListSelected =
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForCustomList;

    const totalRecipientCount =
      executiveWeeklyCheckInCompanyCustomList.length +
      externalExecutiveReportRecipients.length;

    const selectedValue = isCustomListSelected ? (
      <>
        <p>Enabled for custom list</p>
        <p>
          ({totalRecipientCount} {getPeopleOrPerson(totalRecipientCount)})
        </p>
      </>
    ) : (
      "Enabled for all admins"
    );

    return (
      <Dropdown
        onSelect={(key) => {
          if (key === "allAdmin") {
            return setShowConfigureWeeklyExecutiveCheckinReportModal({
              isOpen: true,
              enabledForAllAdmins: true,
            });
          }
          if (key === "create-custom-list" || key === "edit-custom-list") {
            setShowConfigureWeeklyExecutiveCheckinReportModal({
              isOpen: true,
            });
          }
        }}
      >
        <Dropdown.Toggle
          variant="light"
          className="d-flex align-items-center gap-2"
        >
          {selectedValue}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item eventKey="allAdmin">
            {getDropdownCheck(!isCustomListSelected)}
            Enabled for all admins
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            eventKey="create-custom-list"
            className="d-flex align-items-center"
          >
            {getCustomListCheckValue(
              !!isCustomListSelected,
              !!executiveCustomListDropdownText
            )}
            <div>
              {executiveCustomListDropdownText ? (
                <>
                  <p>Custom List</p>
                  <p className="small-grey-text">
                    {executiveCustomListDropdownText}
                  </p>
                </>
              ) : (
                <p>Create Custom List</p>
              )}
            </div>
          </Dropdown.Item>
          {executiveCustomListDropdownText && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="edit-custom-list">
                <FontAwesomeIcon icon="edit" className="me-2" />
                Edit Custom List
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const onEmailReportToggle = async () => {
    if (!showEmailReportToggleWarning.isOpen) {
      return null;
    }
    const payload: Partial<WeeklyCheckInCompanySettings> = {};
    if (showEmailReportToggleWarning.reportType === "seniorManager") {
      payload.hasWeeklyCheckInSeniorManagerReportEnabled =
        showEmailReportToggleWarning.changingTo ? 1 : 0;
    }
    if (showEmailReportToggleWarning.reportType === "teamLeader") {
      payload.hasWeeklyCheckInTeamLeaderReportEnabled =
        showEmailReportToggleWarning.changingTo ? 1 : 0;
    }
    dispatch(updateWeeklyCheckInCompanySettings(payload));
    setShowEmailReportToggleWarning({
      isOpen: false,
    });
  };

  const handleShowVisibilityModal = (value: string) => {
    setVisibilityDefaultModalState({
      isOpen: true,
      type: value as VisibilityDefaultType,
    });
  };

  const getPrivacyFocusedPrivacyDefault = () => {
    const isPrivacyFocusedSelected =
      visibilitySetting === WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY;
    return (
      <div className="row-gap-8px">
        <Button
          variant="link"
          onClick={() =>
            setIsPrivacyFocusedSettingVisible(!isPrivacyFocusedSettingVisible)
          }
        >
          {isPrivacyFocusedSettingVisible ? "Show less" : "Show more options"}
        </Button>
        {isPrivacyFocusedSelected && !isPrivacyFocusedSettingVisible && (
          <div className="visibility-default-selected-label-tag">
            Privacy-Focused selected
          </div>
        )}
      </div>
    );
  };

  const getVisibilityDefaultSection = () => (
    <Card style={{ padding: "16px" }} className="column-gap-16px">
      <h3 className="admin-console-settings-card-subheader">
        Visibility Defaults
      </h3>
      <div className="setting-row-description">
        <h3>Choose privacy defaults for Manager Check-in reports:</h3>
        <p>
          This will set privacy defaults for each Manager Check-in report type
          across your organization.
        </p>
      </div>
      <div className="weekly-checkin-visibility-settings-container">
        {getVisibilityDefaultOption({
          title:
            WEEKLY_CHECK_IN_VISIBILITY_LABELS[
              WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM
            ],
          description:
            "Strong team development, moderate organizational impact. Visibility by admins, select managers and team leaders.",
          value: WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM,
          iconProp: ["fal", "screen-users"],
          setShowVisibilityModal: handleShowVisibilityModal,
          visibilitySetting: visibilitySetting,
        })}
        {getVisibilityDefaultOption({
          title:
            WEEKLY_CHECK_IN_VISIBILITY_LABELS[
              WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT
            ],
          recommended: true,
          description:
            "Highest ROI, both individual and organizational improvements. Visibility by all admins, managers, and leaders.",
          iconProp: ["fal", "chart-mixed"],
          value: WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT,
          setShowVisibilityModal: handleShowVisibilityModal,
          visibilitySetting: visibilitySetting,
        })}
        {isPrivacyFocusedSettingVisible
          ? getVisibilityDefaultOption({
              title:
                WEEKLY_CHECK_IN_VISIBILITY_LABELS[
                  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY
                ],
              description:
                "Good individual growth, limited organizational impact. Visibility by admins and managers only.",
              value: WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY,
              iconProp: ["fal", "user-lock"],
              setShowVisibilityModal: handleShowVisibilityModal,
              visibilitySetting: visibilitySetting,
            })
          : null}
      </div>
      {getPrivacyFocusedPrivacyDefault()}
    </Card>
  );

  const getEnableSeniorManagerReportSetting = (isLoading: boolean) => {
    const isSettingLocked =
      weeklyCheckInCompanySettings?.visibilityDefault ===
        WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY ||
      weeklyCheckInCompanySettings?.visibilityDefault ===
        WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM;
    const isToggleOn =
      !!weeklyCheckInCompanySettings?.hasWeeklyCheckInSeniorManagerReportEnabled;
    return (
      <>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable Senior Manager Check-in Report</h3>
            <div>
              <p className="inline">
                Managers of managers with {WEEKLY_CHECK_IN_FEATURE_NAME}s
                enabled for their direct reports or reporting structure will
                receive an email after each check-in period with key patterns,
                recommended actions, and a completion analysis based on
                check-ins completed by their reports.{" "}
              </p>
              <Button
                variant="link"
                className="inline align-baseline"
                onClick={() => {
                  onOpenPreviewEmailModal("senior-manager-report");
                }}
              >
                Preview email
              </Button>
            </div>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={isToggleOn}
              handleToggle={() => {
                setShowEmailReportToggleWarning({
                  isOpen: true,
                  reportType: "seniorManager",
                  changingTo:
                    !weeklyCheckInCompanySettings?.hasWeeklyCheckInSeniorManagerReportEnabled,
                });
              }}
              disabled={isLoading || isSettingLocked}
            />
          </div>
        </div>
        {isToggleOn && <SeniorManagerReportSettingsSection />}
      </>
    );
  };
  const getEnableTeamLeaderReportSetting = (isLoading: boolean) => {
    const isSettingLocked =
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY;
    const isToggleOn =
      !!weeklyCheckInCompanySettings?.hasWeeklyCheckInTeamLeaderReportEnabled;

    return (
      <>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable Team Leader Check-in Report</h3>
            <div>
              <p className="inline">
                Leaders of teams with {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled
                will receive an email after each check-in period with key
                patterns, recommended actions, and a completion analysis based
                on check-ins completed by their team members.{" "}
              </p>
              <Button
                variant="link"
                className="inline align-baseline"
                onClick={() => {
                  onOpenPreviewEmailModal("team-leader-report");
                }}
              >
                Preview email
              </Button>
            </div>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={isToggleOn}
              handleToggle={() => {
                setShowEmailReportToggleWarning({
                  isOpen: true,
                  reportType: "teamLeader",
                  changingTo:
                    !weeklyCheckInCompanySettings?.hasWeeklyCheckInTeamLeaderReportEnabled,
                });
              }}
              disabled={isLoading || isSettingLocked}
            />
          </div>
        </div>
        {isToggleOn && <TeamLeaderReportSettingsSection />}
      </>
    );
  };
  const getEnableWeeklyExecutiveReportSetting = (isLoading: boolean) => {
    const isExecutiveFeatureToggleEnabled = !!(
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForAllAdmins ||
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForCustomList
    );
    return (
      <div className="setting-row">
        <div
          className="setting-row-description"
          id="check-in-with-coach-bo-setting"
        >
          <h3>Enable Executive Check-in Report</h3>
          <div>
            <p className="inline">
              Executives will receive an email after each check-in period with
              organization wide insights, themes, and sentiments. This report
              also includes department level updates, a spotlight for successes,
              and people to connect with for the period.{" "}
            </p>
            <Button
              variant="link"
              className="inline align-baseline"
              onClick={() => {
                onOpenPreviewEmailModal("executive-report");
              }}
            >
              Preview email
            </Button>
          </div>
        </div>
        <div className="setting-row-action">
          {isExecutiveFeatureToggleEnabled
            ? getCustomListDropdownForExecutiveCheckInReport()
            : null}
          <Toggle
            isOn={isExecutiveFeatureToggleEnabled}
            handleToggle={() => {
              if (!isExecutiveFeatureToggleEnabled) {
                return setShowConfigureWeeklyExecutiveCheckinReportModal({
                  isOpen: true,
                  enabledForAllAdmins:
                    !executiveWeeklyCheckInCompanyCustomList?.length,
                });
              }

              dispatch(
                updateWeeklyCheckInCompanySettings({
                  hasWeeklyCheckInExecutiveReportEnabledForAllAdmins: 0,
                  hasWeeklyCheckInExecutiveReportEnabledForCustomList: 0,
                })
              );
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  };

  const getHarmDetectionNotificationModeDropdown = () => {
    const currentMode =
      weeklyCheckInCompanySettings?.harmDetectionNotificationMode ??
      "admins_only";

    const modeLabels: Record<
      NonNullable<
        WeeklyCheckInCompanySettings["harmDetectionNotificationMode"]
      >,
      string
    > = {
      all_with_access: "All Managers & Admins",
      admins_only: "Admins Only",
      specific_admins: "Specific Admins",
    };

    const specificAdminsCount = harmDetectionAlertRecipients.length;
    const selectedLabel =
      currentMode === "specific_admins" && specificAdminsCount > 0 ? (
        <>
          <p>Specific Admins</p>
          <p>
            ({specificAdminsCount} {getPeopleOrPerson(specificAdminsCount)})
          </p>
        </>
      ) : (
        modeLabels[currentMode]
      );

    return (
      <Dropdown
        onSelect={(key) => {
          if (!key) return;
          if (key === "specific_admins") {
            return setShowHarmDetectionRecipientsModal(true);
          }
          dispatch(
            updateWeeklyCheckInCompanySettings({
              harmDetectionNotificationMode: key as
                | "all_with_access"
                | "admins_only",
            })
          );
        }}
      >
        <Dropdown.Toggle
          variant="light"
          className="d-flex align-items-center gap-2"
        >
          {selectedLabel}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item eventKey="all_with_access">
            {getDropdownCheck(currentMode === "all_with_access")}
            All Managers & Admins
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="admins_only">
            {getDropdownCheck(currentMode === "admins_only")}
            Admins Only
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            eventKey="specific_admins"
            className="d-flex align-items-center"
          >
            {getDropdownCheck(currentMode === "specific_admins")}
            <div>
              {currentMode === "specific_admins" && specificAdminsCount > 0 ? (
                <>
                  <p>Specific Admins</p>
                  <p className="small-grey-text">
                    ({specificAdminsCount}{" "}
                    {getPeopleOrPerson(specificAdminsCount)})
                  </p>
                </>
              ) : (
                <p>Specific Admins</p>
              )}
            </div>
          </Dropdown.Item>
          {currentMode === "specific_admins" && specificAdminsCount > 0 && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                eventKey="specific_admins"
                onClick={() => setShowHarmDetectionRecipientsModal(true)}
              >
                <FontAwesomeIcon icon="edit" className="me-2" />
                Edit Recipients
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getEnableHarmDetectionSetting = (isLoading: boolean) => {
    if (!isAIServiceChatEnabled || !isCheckInHarmDetectionEnabled) {
      return null;
    }

    const isHarmDetectionEnabled =
      !!weeklyCheckInCompanySettings?.isHarmfulDetectionEnabledForCheckIns;

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Enable Harmful Content Detection</h3>
          <p>
            AI scans check-in responses for possible safety or HR concerns (e.g.
            self-harm, harassment, discrimination) as a supplemental signal. It
            is not a substitute for your existing reporting channels and may
            miss concerns or flag false positives.
          </p>
        </div>
        <div className="setting-row-action">
          {isHarmDetectionEnabled
            ? getHarmDetectionNotificationModeDropdown()
            : null}
          <Toggle
            isOn={isHarmDetectionEnabled}
            handleToggle={() => {
              if (isHarmDetectionEnabled) {
                dispatch(
                  updateWeeklyCheckInCompanySettings({
                    isHarmfulDetectionEnabledForCheckIns: 0,
                  })
                );
                return;
              }
              setShowHarmDetectionWarningModal(true);
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  };

  const getReportVisibilitySetting = (isLoading: boolean) => {
    const toggleValue =
      weeklyCheckInCompanySettings?.reportVisibilitySettings ??
      "individual-and-admin";

    const dropdownConstant: Record<
      NonNullable<WeeklyCheckInCompanySettings["reportVisibilitySettings"]>,
      string
    > = {
      "individual-and-admin": "Individual receiving this report and admins",
      "everyone-with-analyses-access":
        "Anyone who can see conversation summaries",
    };

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Report Visibility Settings</h3>
          <p>
            If "{dropdownConstant["everyone-with-analyses-access"]}" is
            selected, user’s who have access to view another user’s response
            summary will also be able to view any reports that user receives.
            This helps senior managers better coach their reports.
          </p>
        </div>
        <div className="setting-row-action">
          <Dropdown
            onSelect={(key) => {
              dispatch(
                updateWeeklyCheckInCompanySettings({
                  reportVisibilitySettings: key as
                    | "individual-and-admin"
                    | "everyone-with-analyses-access",
                })
              );
            }}
          >
            <Dropdown.Toggle
              variant="light"
              className="d-flex align-items-center gap-2"
              disabled={isLoading}
            >
              {dropdownConstant[toggleValue]}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="individual-and-admin">
                {dropdownConstant["individual-and-admin"]}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="everyone-with-analyses-access">
                {dropdownConstant["everyone-with-analyses-access"]}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  };

  const getWeeklyCheckInWarningInfoBanner = () => {
    let message: string | null = null;
    if (
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY
    ) {
      message =
        "Senior Manager Check-in Reports and Team Leader Check-in Reports cannot be enabled due to visibility defaults set below. To enable Team Leader Check-in Reports, your organization must have at least Team-Focused visibility selected. To enable Senior Manager Check-in Reports, your organization must have Development-Focused visibility selected.";
    } else if (
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM
    ) {
      message =
        "Senior Manager Check-in Reports cannot be enabled due to visibility defaults set below. To enable this report, your organization must have Development-Focused visibility selected.";
    }

    if (!message) return null;

    return (
      <div className="warning-banner yellow">
        <div className="row-gap-8px align-items-center">
          <FontAwesomeIcon icon={["far", "lock-keyhole"]} />
          <p>{message}</p>
        </div>
      </div>
    );
  };

  const onWeeklyCheckInToggle = (updatedWeeklyCheckInToggleValue: boolean) => {
    if (updatedWeeklyCheckInToggleValue) {
      return dispatch(
        updateWeeklyCheckInCompanySettings({
          hasWeeklyCheckInsEnabled: 1,
          hasWeeklyCheckInsEnabledForEntireCompany: 0,
        })
      );
    }

    const hasCustomListSaved = !!(
      companyWeeklyCheckInCustomList?.managers.length ??
      companyWeeklyCheckInCustomList?.teams.length ??
      companyWeeklyCheckInCustomList?.departments.length
    );

    // if no custom list has yet been saved and the setting for hasWeeklyCheckInsEnabled is enabled then just disable it
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled &&
      !hasCustomListSaved &&
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return dispatch(
        updateWeeklyCheckInCompanySettings({
          hasWeeklyCheckInsEnabledForEntireCompany: 0,
          hasWeeklyCheckInsEnabled: 0,
        })
      );
    }

    setShowDisableWeeklyCheckInConfirmationModal(true);
  };

  const onOpenPreviewEmailModal = (type: CheckInEmailReportType) =>
    dispatch(
      setSampleWeeklyCheckInEmailReportModalState({
        isOpen: true,
        type,
      })
    );

  const getWhoSeesWeeklyCheckInMerchandiseValue = () => {
    const isAllChecked =
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForAdmins &&
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForTeamLeaders &&
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForManagers;

    return {
      everyone: !!isAllChecked,
      admin: !!weeklyCheckInCompanySettings?.hasMerchandisingEnabledForAdmins,
      teamLeaders:
        !!weeklyCheckInCompanySettings?.hasMerchandisingEnabledForTeamLeaders,
      managers:
        !!weeklyCheckInCompanySettings?.hasMerchandisingEnabledForManagers,
    };
  };

  const onWhoSeesWeeklyCheckInMerchandiseChange = (
    selectedItems: SelectedItems,
    changedKey?: string | number
  ) => {
    const newValue = !!(changedKey && selectedItems[changedKey]);
    switch (changedKey) {
      case "admin":
        return dispatch(
          updateWeeklyCheckInCompanySettings({
            hasMerchandisingEnabledForAdmins: newValue,
          })
        );
      case "teamLeaders":
        return dispatch(
          updateWeeklyCheckInCompanySettings({
            hasMerchandisingEnabledForTeamLeaders: newValue,
          })
        );
      case "managers":
        return dispatch(
          updateWeeklyCheckInCompanySettings({
            hasMerchandisingEnabledForManagers: newValue,
          })
        );
      case "everyone":
        return dispatch(
          updateWeeklyCheckInCompanySettings({
            hasMerchandisingEnabledForAdmins: newValue,
            hasMerchandisingEnabledForTeamLeaders: newValue,
            hasMerchandisingEnabledForManagers: newValue,
          })
        );
      default:
        return;
    }
  };

  const getWhoSeesWeeklyCheckInMerchandiseSettingText = () => {
    const isAllChecked =
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForAdmins &&
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForTeamLeaders &&
      weeklyCheckInCompanySettings?.hasMerchandisingEnabledForManagers;

    if (isAllChecked) {
      return "Everyone in this organization";
    }

    if (
      !weeklyCheckInCompanySettings?.hasMerchandisingEnabledForAdmins &&
      !weeklyCheckInCompanySettings?.hasMerchandisingEnabledForTeamLeaders &&
      !weeklyCheckInCompanySettings?.hasMerchandisingEnabledForManagers
    ) {
      return "No one in this organization";
    }

    return undefined;
  };

  const getCadenceCheckInSetting = (isLoading: boolean) => {
    if (!checkInFrequencyEnabled) {
      return false;
    }
    const activeCadence =
      weeklyCheckInCompanySettings?.checkInFrequency ?? "weekly";

    const cadenceOptions = Object.entries(CADENCE_OPTIONS).map(
      ([key, value], idx) => (
        <Fragment key={key}>
          {idx !== 0 && <Dropdown.Divider />}
          <Dropdown.Item eventKey={key}>
            {activeCadence === key ? (
              <FontAwesomeIcon icon={faCheck} />
            ) : (
              <div className="w-[12px]" />
            )}
            {value}
          </Dropdown.Item>
        </Fragment>
      )
    );

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Check-In Cadence</h3>
          <p>
            Control how often {WEEKLY_CHECK_IN_FEATURE_NAME}s are sent to
            employees. Choose weekly, biweekly, or monthly to match your team's
            workflow.
          </p>
        </div>
        <div className="setting-row-action">
          <Dropdown
            onSelect={(key) => {
              if (!key || key === activeCadence) return;
              setWeeklyCheckInCadenceModalState({
                incomingCadence: key as WeeklyCheckInCadence,
              });
            }}
          >
            <Dropdown.Toggle
              variant="light"
              className="d-flex align-items-center gap-2"
              disabled={isLoading}
            >
              {CADENCE_OPTIONS[activeCadence]}
            </Dropdown.Toggle>
            <Dropdown.Menu>{cadenceOptions}</Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  };

  const getWeeklyCheckInVisibilitySettingSection = () => {
    const getListItems = (mode: "conservative" | "open") => {
      if (!visibilityModeCardCollapseState) {
        return null;
      }
      return (
        <div className="d-flex flex-col gap-[6px]">
          {WEEKLY_CHECK_IN_VISIBILITY_MODE_LIST_ITEMS[mode].map((item) => (
            <div key={item.label} className="d-flex gap-2 align-items-center">
              <p
                className={
                  item.isCheck ? "text-charcoal-130" : "text-charcoal-60"
                }
              >
                <FontAwesomeIcon
                  icon={item.isCheck ? faCheck : faXmark}
                  className={cn(
                    "me-2",
                    item.isCheck ? "text-success-green-100" : ""
                  )}
                />
                <span>{item.label}</span>
              </p>
            </div>
          ))}
        </div>
      );
    };

    const buttonClassName = cn(
      "border-[2px] rounded-[8px] p-[20px] text-left transition-colors d-flex flex-col gap-2",
      "[&.active]:border-sapphire-100 [&.active]:bg-sapphire-05",
      "[&.inactive]:border-charcoal-20",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      "[&.inactive]:hover:border-charcoal-40"
    );
    const isModeOpen = weeklyCheckInCompanySettings?.visibilityMode === "open";

    return (
      <div>
        <h3 className="text-black">Score Visibility Mode</h3>
        <p className="text-charcoal-100 mb-2">
          Choose how much check-in data is visible to admins and managers. This
          applies organization-wide across the Responses table, Dashboard,
          detail panels, and reports.
        </p>
        <div className="d-grid gap-2 grid-cols-1 md:grid-cols-2">
          <button
            onClick={() => {
              if (weeklyCheckInCompanySettings?.visibilityMode !== "open") {
                return;
              }
              dispatch(
                updateWeeklyCheckInCompanySettings({
                  visibilityMode: "conservative",
                })
              );
            }}
            disabled={updateWeeklyCheckInCompanySettingsStatus === "loading"}
            className={cn(buttonClassName, isModeOpen ? "inactive" : "active")}
            type="button"
          >
            <div className="d-flex gap-2">
              <FontAwesomeIcon icon={faShield} className="mt-1 text-2xl" />
              <div>
                <h3 className="text-black">Conservative</h3>
                <p className="text-charcoal-80">
                  Privacy-first. No individual scores visible to anyone. Best
                  for risk-averse orgs or early-stage trust building.
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setVisibilityModeCardCollapseState((prev) => !prev);
                }}
                type="button"
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={cn(
                    "transition-transform",
                    visibilityModeCardCollapseState ? "rotate-180" : ""
                  )}
                />
              </button>
            </div>
            {getListItems("conservative")}
          </button>
          <button
            onClick={() => {
              if (weeklyCheckInCompanySettings?.visibilityMode === "open") {
                return;
              }
              setShowWeeklyCheckInVisibilityWarningModal(true);
            }}
            disabled={updateWeeklyCheckInCompanySettingsStatus === "loading"}
            className={cn(buttonClassName, isModeOpen ? "active" : "inactive")}
            type="button"
          >
            <div className="d-flex gap-2">
              <FontAwesomeIcon icon={faEye} className="mt-1 text-2xl" />
              <div>
                <div className="d-flex gap-2">
                  <h3 className="text-black">Open</h3>
                  <p className="bg-sapphire-10 text-sapphire-100 px-2 py-1 rounded-[8px]">
                    Default
                  </p>
                </div>
                <p className="text-charcoal-80">
                  Full visibility into scores, trends, and AI insights. Best for
                  open, growth-oriented cultures.
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setVisibilityModeCardCollapseState((prev) => !prev);
                }}
                type="button"
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={cn(
                    "transition-transform",
                    visibilityModeCardCollapseState ? "rotate-180" : ""
                  )}
                />
              </button>
            </div>
            {getListItems("open")}
          </button>
        </div>
      </div>
    );
  };

  const getAdminOnlyCheckInEditingSetting = (isLoading: boolean) => {
    const isToggleOn =
      weeklyCheckInCompanySettings?.nonAdminsCanEditCheckInRecipients !== false;
    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Allow Non-Admins to Edit Check-In Recipients</h3>
          <p>
            When enabled, managers can turn on {WEEKLY_CHECK_IN_FEATURE_NAME}s
            for their direct reports or reporting structure, and team leaders
            can enable {WEEKLY_CHECK_IN_FEATURE_NAME}s for their teams. Disable
            this to restrict these controls to admins only.
          </p>
        </div>
        <div className="setting-row-action">
          <Toggle
            isOn={isToggleOn}
            handleToggle={() => {
              dispatch(
                updateWeeklyCheckInCompanySettings({
                  nonAdminsCanEditCheckInRecipients: !isToggleOn,
                })
              );
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  };

  const getCheckInWithCoachBoSetting = () => {
    if (!areAIFeaturesEnabled || !weeklyCheckInConfigCatFlag) return null;
    if (getWeeklyCheckInCompanySettingsStatus === "loading") {
      return <Loading />;
    }

    const isLoading = updateWeeklyCheckInCompanySettingsStatus === "loading";
    const isFeatureToggleEnabled = !!(
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany ||
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled
    );

    return (
      <>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable {WEEKLY_CHECK_IN_FEATURE_NAME}s</h3>
            <div>
              <p className="inline">
                Users with {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled will receive
                an email from Coach Bo to check on work progress, share how
                they’re feeling, and report any successes or challenges at the
                end of each check-in period.{" "}
              </p>
              <Button
                variant="link"
                className="inline align-baseline"
                onClick={() => {
                  onOpenPreviewEmailModal("check-in-email");
                }}
              >
                Preview email
              </Button>
            </div>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={isFeatureToggleEnabled}
              disabled={isLoading}
              handleToggle={() =>
                onWeeklyCheckInToggle(!isFeatureToggleEnabled)
              }
            />
          </div>
        </div>

        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Who sees {WEEKLY_CHECK_IN_FEATURE_NAME}s merchandising? </h3>
            <p>
              This setting controls which users across your organization can
              view promotional materials, setup guidance, and support resources
              for the {WEEKLY_CHECK_IN_FEATURE_NAME}s feature within the Develop
              product.
            </p>
          </div>
          <div className="setting-row-action">
            <div>
              <MultiSelectDropdown
                checklistItems={[
                  {
                    id: "admin",
                    label: "Admins",
                  },
                  {
                    id: "teamLeaders",
                    label: "Team leaders",
                  },
                  {
                    id: "managers",
                    label: "Managers",
                  },
                  {
                    id: "everyone",
                    label: "Everyone in this organization",
                  },
                ]}
                selectedItems={getWhoSeesWeeklyCheckInMerchandiseValue()}
                setSelectedItems={onWhoSeesWeeklyCheckInMerchandiseChange}
                forcedDropdownText={getWhoSeesWeeklyCheckInMerchandiseSettingText()}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
        {isFeatureToggleEnabled && getWeeklyCheckInVisibilitySettingSection()}

        {isFeatureToggleEnabled ? (
          <>
            <div className="setting-row">
              <div className="setting-row-description">
                <h3>Who Is Receiving {WEEKLY_CHECK_IN_FEATURE_NAME}s?</h3>
                <p>
                  This list shows all users with {WEEKLY_CHECK_IN_FEATURE_NAME}s
                  enabled. Check-ins can be enabled by you, other admins,
                  managers, or team leaders in this organization.
                </p>
              </div>
              <div className="setting-row-action">
                <Dropdown onSelect={onCustomListDropdownSelection}>
                  <Dropdown.Toggle
                    variant="light"
                    className="d-flex align-items-center gap-2"
                  >
                    {getWhoIsReceivingCheckInSelectedValue()}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {getWhoIsReceivingCheckInDropdownOptions()}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            {getEnableHarmDetectionSetting(isLoading)}
            {getAdminOnlyCheckInEditingSetting(isLoading)}
            {aiServiceChatEnabled && getCadenceCheckInSetting(isLoading)}

            {getEnableSeniorManagerReportSetting(isLoading)}
            {getEnableTeamLeaderReportSetting(isLoading)}
            {getEnableWeeklyExecutiveReportSetting(isLoading)}
            {getReportVisibilitySetting(isLoading)}
            {getWeeklyCheckInWarningInfoBanner()}
            {getVisibilityDefaultSection()}
          </>
        ) : null}
      </>
    );
  };

  if (!areAIFeaturesEnabled || !weeklyCheckInConfigCatFlag) {
    return null;
  }

  return (
    <Card className="admin-console-settings-card" id="weekly-check-in-setting">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h2 id="visibility-settings">{WEEKLY_CHECK_IN_FEATURE_NAME}s</h2>
          <p>
            Settings related to the {WEEKLY_CHECK_IN_FEATURE_NAME} with Coach
            Bo.
          </p>
        </div>
      </div>
      <CheckInCustomListModal
        isOpen={showCreateCustomListModal.isOpen}
        onClose={() => setShowCreateCustomListModal({ isOpen: false })}
        modalDescription={`Search and add departments and/or teams you want to enable ${WEEKLY_CHECK_IN_FEATURE_NAME}s for. To enable Check-Ins for managers and their direct reports or their entire reporting structure, choose “Select managers” and add managers first. You can enable Check-Ins for their direct reports or their entire reporting structure.`}
        entitiesShowing={["managers", "teams"]}
        teamIds={companyWeeklyCheckInCustomList?.teams}
        departmentIds={companyWeeklyCheckInCustomList?.departments}
        managers={companyWeeklyCheckInCustomList?.managers}
        title={`Who Is Receiving ${WEEKLY_CHECK_IN_FEATURE_NAME}s?`}
      />
      <ConfigureWeeklyExecutiveCheckinReportModal
        show={showConfigureWeeklyExecutiveCheckinReportModal.isOpen}
        onHide={() => {
          setShowConfigureWeeklyExecutiveCheckinReportModal({ isOpen: false });
        }}
        areAllAdminsSelected={
          showConfigureWeeklyExecutiveCheckinReportModal.enabledForAllAdmins
        }
      />
      <CheckinCustomListConfirmationModal
        show={showWeeklyCheckInConfirmationModalState.isOpen}
        onHide={() =>
          setShowWeeklyCheckInConfirmationModalState({ isOpen: false })
        }
        teamIds={companyWeeklyCheckInCustomList?.teams}
        departmentIds={companyWeeklyCheckInCustomList?.departments}
        managers={companyWeeklyCheckInCustomList?.managers}
        isCompanyWide={
          showWeeklyCheckInConfirmationModalState.enabledForEveryone
        }
        onSuccess={() => {
          setShowWeeklyCheckInConfirmationModalState({ isOpen: false });
        }}
      />
      <EmailReportToggleWarning
        isOpen={showEmailReportToggleWarning.isOpen}
        onHide={() => setShowEmailReportToggleWarning({ isOpen: false })}
        onConfirmClick={onEmailReportToggle}
        reportType={showEmailReportToggleWarning.reportType}
        valueChangingTo={showEmailReportToggleWarning.changingTo}
      />
      <DisableWeeklyCheckInConfirmationModal
        show={showDisableWeeklyCheckInConfirmationModal}
        onHide={() => setShowDisableWeeklyCheckInConfirmationModal(false)}
      />
      <WeeklyCheckInVisibilityDefaultsModal
        isOpen={visibilityDefaultModalState.isOpen}
        onClose={() => setVisibilityDefaultModalState({ isOpen: false })}
        type={visibilityDefaultModalState.type}
        setType={(type) =>
          setVisibilityDefaultModalState({ isOpen: true, type })
        }
      />
      <WeeklyCheckInVisibilityWarningModal
        isOpen={showWeeklyCheckInVisibilityWarningModal}
        onClose={() => setShowWeeklyCheckInVisibilityWarningModal(false)}
        onConfirm={async () => {
          await dispatch(
            updateWeeklyCheckInCompanySettings({ visibilityMode: "open" })
          );
          setShowWeeklyCheckInVisibilityWarningModal(false);
        }}
        isLoading={updateWeeklyCheckInCompanySettingsStatus === "loading"}
      />
      <UpdateCheckInCadenceModal
        show={!!weeklyCheckInCadenceModalState}
        onHide={() => setWeeklyCheckInCadenceModalState(null)}
        newCheckInCadence={weeklyCheckInCadenceModalState?.incomingCadence}
        existingCheckInCadence={weeklyCheckInCompanySettings?.checkInFrequency}
      />
      <HarmDetectionWarningModal
        isOpen={showHarmDetectionWarningModal}
        onClose={() => setShowHarmDetectionWarningModal(false)}
        onConfirm={async () => {
          await dispatch(
            updateWeeklyCheckInCompanySettings({
              isHarmfulDetectionEnabledForCheckIns: 1,
            })
          );
          setShowHarmDetectionWarningModal(false);
        }}
        isLoading={updateWeeklyCheckInCompanySettingsStatus === "loading"}
      />
      <HarmDetectionRecipientsModal
        show={showHarmDetectionRecipientsModal}
        onHide={() => setShowHarmDetectionRecipientsModal(false)}
      />
      {getCheckInWithCoachBoSetting()}
    </Card>
  );
};

export default WeeklyCheckInAdminConsoleSettings;
